import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { Controller, FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { BasicButton } from '../../../components/common/Button';
import { InputWord } from '../../../components/common/Input';
import { PasswordConstraint } from '../../../components/common/PasswordConstraint';
import FormHeader from '../../../components/forms/FormHeader';
import { updateSkemeetPasswordSchema, updateSkemeetPasswordType } from '../../../schema/LoginSchema';
import AuthService from '../../../services/auth.service';

type UpdateSkemeetPasswordFormProps = {
  connexionMethods: UseFormReturn<{ nickNameOrEmail: string; password: string }, undefined>;
  skemeetUserInfos: { temporaryAccessToken: string; firstname: string; lastname: string; cellphone: string };
  redirect: (check2FA?: boolean) => void;
};

const UpdateSkemeetPasswordForm = ({ connexionMethods, skemeetUserInfos, redirect }: UpdateSkemeetPasswordFormProps) => {
  const authService = new AuthService();

  const updateSkemeetPasswordMethods = useForm<updateSkemeetPasswordType>({
    resolver: yupResolver(updateSkemeetPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const [passwordFocus, setPasswordFocus] = useState<boolean>(false);

  const updateSkemeetPassword = async ({ password }: updateSkemeetPasswordType) => {
    await authService.createUserFromSkemeetAndLogin({
      email: connexionMethods.watch('nickNameOrEmail'),
      password,
      cellphone: skemeetUserInfos.cellphone,
      firstname: skemeetUserInfos.firstname,
      lastname: skemeetUserInfos.lastname,
    });

    redirect();
  };

  return (
    <>
      <FormHeader
        title={`Mise à jour de votre mot de passe`}
        subTitle={`Pour des raisons de sécurité, nous vous demandons de renforcer votre mot de passe afin qu'il contienne : 12 caractères, une lettre majuscule et minuscule, un chiffre et un caractère spécial.`}
      />
      <FormProvider {...updateSkemeetPasswordMethods}>
        <form onSubmit={updateSkemeetPasswordMethods.handleSubmit(updateSkemeetPassword)}>
          <Controller
            name='password'
            control={updateSkemeetPasswordMethods.control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Nouveau mot de passe'
                  type='password'
                  autoComplete='off'
                  mandatory={true}
                  placeholder='Mot de passe'
                  value={value}
                  onChangeValue={onChange}
                  errorMessage={error?.message}
                  onFocus={() => {
                    setPasswordFocus(true);
                  }}
                  onBlur={() => {
                    setPasswordFocus(false);
                    onBlur();
                  }}
                />
              </>
            )}
          />
          {passwordFocus && <PasswordConstraint password={updateSkemeetPasswordMethods.watch('password')} />}
          <Controller
            name='confirmPassword'
            control={updateSkemeetPasswordMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Confirmer votre mot de passe'
                  type='password'
                  autoComplete='off'
                  placeholder='Mot de passe'
                  value={value}
                  onChangeValue={onChange}
                  errorMessage={error?.message}
                />
              </>
            )}
          />
          <BasicButton label='Mettre à jour mon mot de passe' fullWidth={true} isSubmitButton={true} />
          <div
            onClick={() => {
              redirect();
            }}
            className='text-green-secondary mt-3 cursor-pointer text-center font-bold hover:underline'
          >
            Me le rappeler plus tard
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default UpdateSkemeetPasswordForm;
